import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/utils";
import { X } from "lucide-react";
import type { ReactNode } from "react";

export type VersionHistoryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Host callback for portaled editor previews (Notion-like files). */
  onPreviewContainer?: (node: HTMLDivElement | null) => void;
  /** Inline preview content (API-backed files). */
  preview?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

/**
 * Notion-style version history: near-fullscreen split view with document
 * preview on the left and a version sidebar on the right.
 */
export function VersionHistoryDialog({
  open,
  onOpenChange,
  title = "Version history",
  onPreviewContainer,
  preview,
  children,
  footer,
  className,
}: VersionHistoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        className={cn(
          "flex h-[80vh] w-full max-h-[90vh] max-w-6xl flex-row gap-0 overflow-hidden p-0 sm:max-w-6xl",
          className,
        )}
        hideCloseButton
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
          <div
            ref={onPreviewContainer}
            className="h-full min-h-0 flex-1 overflow-auto"
          >
            {preview}
          </div>
        </div>

        <aside className="flex w-[min(300px,32vw)] shrink-0 flex-col border-l bg-background">
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
            <DialogTitle className="text-sm font-semibold">{title}</DialogTitle>
            <button
              type="button"
              className="rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
              aria-label="Close version history"
              onClick={() => onOpenChange(false)}
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {children}
          </div>

          {footer ? (
            <div className="flex shrink-0 justify-end border-t px-4 py-3">
              {footer}
            </div>
          ) : null}
        </aside>
      </DialogContent>
    </Dialog>
  );
}

export function VersionHistoryListItem({
  active,
  title,
  subtitle,
  onClick,
  trailing,
}: {
  active?: boolean;
  title: string;
  subtitle?: string;
  onClick: () => void;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className={cn(
          "mb-0.5 flex min-w-0 flex-1 cursor-pointer flex-col rounded-[10px] border border-transparent px-2.5 py-2 text-left text-inherit",
          "hover:bg-muted/50",
          active && "border-primary/45 bg-primary/8",
        )}
        onClick={onClick}
      >
        <div className="text-xs font-semibold leading-[1.2]">{title}</div>
        {subtitle ? (
          <div className="mt-1 text-[11px] text-muted-foreground">
            {subtitle}
          </div>
        ) : null}
      </button>
      {trailing}
    </div>
  );
}
